import { useMemo, useState } from "react";
import { styles } from "../styles";
import BackButton from "../components/BackButton";

const isAdminOrManager = (role) =>
  role === "executive" || role === "facilities_manager";

export default function FacilityServiceTicketsPage({
  facilityServiceTicketsRaw = [],
  jobsRaw = [],
  customers = [],
  actions,
  user,
  role,
  onSelectJob,
  onBack,
}) {
  const [statusFilter, setStatusFilter] = useState("unack");
  const [jobFilter, setJobFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const canManage = isAdminOrManager(role);

  // For each ticket, denormalize job + building info for display.
  const enriched = useMemo(() => {
    const jobsById = new Map(jobsRaw.map((j) => [j.id, j]));
    const buildingByJobId = new Map();
    for (const c of customers) {
      for (const b of c.locations || []) {
        for (const j of jobsRaw) {
          if (j.locationId === b.id) {
            buildingByJobId.set(j.id, { building: b, customer: c });
          }
        }
      }
    }
    return facilityServiceTicketsRaw.map((t) => {
      const job = jobsById.get(t.jobId);
      const ctx = buildingByJobId.get(t.jobId);
      return {
        ...t,
        jobNumber: job?.jobNumber || "",
        jobTitle: job?.title || "(work order)",
        buildingName: ctx?.building?.name || "",
        customerName: ctx?.customer?.name || "",
      };
    });
  }, [facilityServiceTicketsRaw, jobsRaw, customers]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (statusFilter === "unack") list = list.filter((t) => !t.acknowledged);
    if (statusFilter === "ack") list = list.filter((t) => t.acknowledged);
    if (jobFilter) list = list.filter((t) => t.jobId === jobFilter);
    return list.sort(
      (a, b) =>
        new Date(b.createdAt || b.workDate) -
        new Date(a.createdAt || a.workDate)
    );
  }, [enriched, statusFilter, jobFilter]);

  const jobOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const t of enriched) {
      if (seen.has(t.jobId)) continue;
      seen.add(t.jobId);
      opts.push({
        id: t.jobId,
        label: `${t.jobNumber ? `#${t.jobNumber} — ` : ""}${t.jobTitle}`,
      });
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [enriched]);

  const acknowledge = (t) =>
    actions.updateFacilityServiceTicket(t.id, {
      acknowledged: true,
      acknowledgedAt: new Date().toISOString(),
      acknowledgedBy: user?.email || "",
    });

  const acknowledgeAll = async () => {
    const targets = filtered.filter((t) => !t.acknowledged);
    if (targets.length === 0) return;
    if (
      !window.confirm(
        `Acknowledge ${targets.length} ticket(s)? Clears the NEW badge / dashboard counter for everyone in your org.`
      )
    )
      return;
    setBusy(true);
    try {
      for (const t of targets) {
        await acknowledge(t);
      }
    } finally {
      setBusy(false);
    }
  };

  const openJob = (t) => {
    const job = jobsRaw.find((j) => j.id === t.jobId);
    if (job && onSelectJob) onSelectJob(job);
  };

  const unackCount = enriched.filter((t) => !t.acknowledged).length;

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Service Tickets</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
        Internal record of work the maintenance team has done across all work
        orders. Click a ticket to open its work order.
      </p>

      <div
        style={{
          ...styles.card,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <label style={{ fontSize: "12px", color: "#666" }}>Status:</label>
        {["unack", "ack", "all"].map((s) => (
          <button
            key={s}
            style={{
              ...styles.button,
              margin: 0,
              background: statusFilter === s ? "#1a5fb4" : "#f3f4f6",
              color: statusFilter === s ? "white" : "#222",
              fontWeight: statusFilter === s ? 600 : 500,
            }}
            onClick={() => setStatusFilter(s)}
          >
            {s === "unack" ? "Unacknowledged" : s === "ack" ? "Acknowledged" : "All"}
          </button>
        ))}

        <label style={{ fontSize: "12px", color: "#666", marginLeft: "10px" }}>
          Job:
        </label>
        <select
          style={{ ...styles.input, margin: 0, maxWidth: "280px" }}
          value={jobFilter}
          onChange={(e) => setJobFilter(e.target.value)}
        >
          <option value="">All work orders</option>
          {jobOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>

        {canManage && unackCount > 0 && (
          <button
            style={{ ...styles.button, marginLeft: "auto" }}
            onClick={acknowledgeAll}
            disabled={busy}
          >
            {busy ? "Working..." : `Acknowledge All (${unackCount})`}
          </button>
        )}
      </div>

      <p style={{ fontSize: "13px", color: "#666", margin: "8px 0" }}>
        {filtered.length} ticket(s)
      </p>

      {filtered.length === 0 ? (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          No tickets match these filters.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {filtered.map((t) => {
            const totalHours = (t.labor || []).reduce(
              (s, l) => s + (parseFloat(l.hours) || 0),
              0
            );
            return (
              <li
                key={t.id}
                style={{ ...styles.card, cursor: "pointer" }}
                onClick={() => openJob(t)}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <strong>
                    {t.jobNumber ? `#${t.jobNumber} — ` : ""}
                    {t.jobTitle}
                  </strong>
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    {t.buildingName}
                  </span>
                  {!t.acknowledged && (
                    <span
                      style={{
                        background: "#fff3cd",
                        color: "#9a6b00",
                        fontSize: "11px",
                        padding: "1px 6px",
                        borderRadius: "8px",
                        fontWeight: 700,
                      }}
                    >
                      NEW
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginLeft: "auto",
                    }}
                  >
                    {t.workDate} · {totalHours} hr ·{" "}
                    {(t.materials || []).length} material(s)
                  </span>
                </div>
                {t.summary && (
                  <div style={{ fontSize: "13px", marginTop: "4px" }}>
                    {t.summary}
                  </div>
                )}
                <div style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>
                  filed by {t.createdByName || t.createdBy || "—"}
                  {t.acknowledged && t.acknowledgedAt && (
                    <>
                      {" · acknowledged "}
                      {new Date(t.acknowledgedAt).toLocaleString()}
                    </>
                  )}
                </div>
                {canManage && !t.acknowledged && (
                  <div style={{ marginTop: "8px" }}>
                    <button
                      style={{ ...styles.button, margin: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        acknowledge(t);
                      }}
                    >
                      Acknowledge
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
